// PDStreakHome.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetch from "../../hooks/useFetch";

import StreakTabs, { TABS, getVisibleGroups } from "./modules/streaks/StreakTabs";
import StreakSummary           from "./modules/streaks/StreakSummary";
import StreakLedger            from "./modules/streaks/StreakLedger";
import MerchantLedger            from "./modules/streaks/MerchantLedger";
import ParentLedger            from "./modules/streaks/MerchantLedger";
import mentorLedger            from "./modules/streaks/MerchantLedger";
import NewStreakInbox          from "./modules/streaks/NewStreakInbox";
import PurchaseClaimForm       from "./modules/streaks/PurchaseClaimForm";
import PurchaseClaimList       from "./modules/streaks/PurchaseClaimList";
import MerchantClaimHistory    from "./modules/streaks/MerchantClaimHistory";
import ShopApprovalCard        from "./modules/streaks/ShopApprovalCard";
import MerchantApprovalHistory    from "./modules/streaks/MerchantApprovalHistory";
import PDCertificateGenerator  from "./modules/streaks/PDCertificateGenerator";
import RedemptionCard          from "./modules/streaks/RedemptionCard";
import RedemptionList          from "./modules/streaks/RedemptionList";
import useStreakSummary        from "./modules/streaks/useStreakSummary";
import PDContest               from "./PDContest";
import PDContestWinners        from "./PDContestWinners";
import PDContestLeaders        from "./PDContestLeaders";
import StreakFlywheel          from "./help/StreakFlywheel";
import StreakSettingsForm      from "./modules/streaks/StreakSettingsForm"

import { generateDummyNewStreaks } from "./modules/streaks/generateDummyNewStreaks";

const PDStreakHome = ({role}) => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const navigate = useNavigate();

    const userId   = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;
    const token    = authState?.token;

    const [selectedTab,    setSelectedTab]    = useState("flywheel");
    const [selected,       setSelected]       = useState(null);
    const [search,         setSearch]         = useState("");

    const [records,        setRecords]        = useState([]);
    const [newStreaks,     setNewStreaks]      = useState([]);
    const [myClaims,       setMyClaims]       = useState([]);
    const [pendingClaims,  setPendingClaims]  = useState([]);
    const [selectedClaims, setSelectedClaims] = useState([]);
    const [requestedRedemption, setRequestedRedemption] = useState([]);
    const [shopBalances,   setShopBalances]   = useState([]);

    const [claimingId,   setClaimingId]   = useState(null);
    const [claimMessage, setClaimMessage] = useState(null);

    useEffect(() => {
        if (authState?.user) {
            setNewStreaks(generateDummyNewStreaks(authState.user));
        }
    }, [authState?.user]);

    useEffect(() => {
        const visibleGroups = getVisibleGroups(role);
        const currentTab = TABS.find(t => t.id === selectedTab);
        if (currentTab && !visibleGroups.includes(currentTab.group)) {
            // Reset to first visible tab for this role
            const firstVisible = TABS.find(t => visibleGroups.includes(t.group) && !t.disabled);
            if (firstVisible) setSelectedTab(firstVisible.id);
        }
    }, [role]);

    useEffect(() => {
        fetchLedger();
        fetchMyClaims();
        fetchPendingClaims();
        fetchShopBalances();
    }, []);

    const fetchLedger = async () => {
        if (!userId || !token) return;
        try {
            const res = await fetchData({
                url: "/pd/streaks/ledger",
                method: "post",
                data: { userId },
                headers: { Authorization: token },
            });
            if (res?.success) setRecords(res.data || []);
        } catch (err) { console.error("Ledger:", err); }
    };

    const fetchMyClaims = async () => {
        try {
            const res = await fetchData({
                url: "/pd/streaks/claim-my",
                method: "post",
                data: { customerId: userId, customerCode: userCode },
                headers: { Authorization: token },
            });
            if (res?.success) setMyClaims(res.data || []);
        } catch (err) { console.error("MyClaims:", err); }
    };

    const fetchPendingClaims = async () => {
        try {
            const res = await fetchData({
                url: "/pd/streaks/claim-pending",
                method: "post",
                data: { shopkeeperCode: userCode },
                headers: { Authorization: token },
            });
            if (res?.success) setPendingClaims(res.data || []);
        } catch (err) { console.error("PendingClaims:", err); }
    };

    const fetchShopBalances = async () => {
        if (!userId) return;
        try {
            const res = await fetchData({
                url: "/pd/streaks/redemption-balances",
                method: "post",
                data: { customerId: userId },
                headers: { Authorization: token },
            });
            if (res?.success) setShopBalances(res.records || []);
        } catch (err) { console.error("ShopBalances:", err); }
    };

    const loadRedemptions = async () => {
        try {
            const res = await fetchData({
                url: "/pd/streaks/redemption-pending",
                method: "post",
                data: { shopkeeperCode: userCode },
                headers: { Authorization: token },
            });
            if (res?.success) setRequestedRedemption(res.records || []);
        } catch (err) { console.error("Redemptions:", err); }
    };

    useEffect(() => {
        if (selectedTab === "approveredeem") loadRedemptions();
        if (selectedTab === "claims") fetchMyClaims();
        if (selectedTab === "approvals") fetchPendingClaims();
    }, [selectedTab]);

    const claimStreak = async (streak) => {
        try {
            setClaimingId(streak._id);
            const user = authState?.user;

            const res = await fetchData({
                url: "/pd/streaks/ledger-post",
                method: "post",
                data: {
                    userId,
                    userCode:      user?.think5whyscode,
                    userName:      user?.name,
                    particulars:   streak.particulars,
                    streakModule:  streak.streakModule,
                    streakType:    streak.streakType,
                    points:        streak.points,
                    streakFromDate: streak.streakFromDate || new Date(), // FIX: correct field
                    streakToDate:   streak.streakToDate  || new Date(),
                    streakBankDate: new Date(),
                    inOutType:     "In",
                    balance:       streak.points,
                    remarks:       "Claimed from New Streaks",
                },
                headers: { Authorization: token },
            });

            if (res?.success) {
                setClaimMessage({ type: "success", text: `🎉 ${streak.points} streak points added!` });
                setNewStreaks((p) => p.filter((i) => i._id !== streak._id));
                await fetchLedger();
            } else {
                setClaimMessage({ type: "warning", text: res?.message || "Already claimed" });
            }
        } catch (err) {
            setClaimMessage({ type: "error", text: "Unable to claim streak" });
        } finally {
            setClaimingId(null);
        }
    };

    const filteredRecords = useMemo(() => {
        const term = search.toLowerCase();
        return (records || []).filter((item) =>
            !term ||
            item.particulars?.toLowerCase().includes(term) ||
            item.userName?.toLowerCase().includes(term) ||
            item.shopName?.toLowerCase().includes(term) ||
            item.inOutType?.toLowerCase().includes(term)
        );
    }, [records, search]);

    const { summary, balance: streakBankBalance } = useStreakSummary(records);

    return (
        <div className="space-y-1 bg-gray-900 p-4 rounded-2xl text-white min-h-screen">

            <StreakTabs
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                navigate={navigate}
                userRole={role}
            />
            
            {selectedTab === "summary" && (
                <StreakSummary
                    summary={summary}
                    balance={streakBankBalance}
                    shopBalances={shopBalances}
                />
            )}

            {selectedTab === "ledger" && (
                <StreakLedger
                    search={search}
                    setSearch={setSearch}
                    records={filteredRecords}
                    selected={selected}
                    setSelected={setSelected}
                />
            )}

            {selectedTab === "merchantledger" && (
                <MerchantLedger />
            )}

            {selectedTab === "parentledger" && (
                <MerchantLedger streakModules={["PurpleDoor", "LifeReplay"]} title="📒 Parent Ledger" />
            )}

            {selectedTab === "mentorledger" && (
                <MerchantLedger streakModules={["PurpleDoor", "LifeReplay"]} title="📒 Mentor Ledger" />
            )}

            {selectedTab === "new" && (
                <NewStreakInbox
                    newStreaks={newStreaks}
                    claimMessage={claimMessage}
                    claimStreak={claimStreak}
                    claimingId={claimingId}
                    setSelectedTab={setSelectedTab}
                />
            )}

            {selectedTab === "claimform" && (
                <div className="space-y-4">
                    <PurchaseClaimForm onSuccess={fetchMyClaims} />
                </div>
            )}

            {selectedTab === "claims" && (
                <div className="space-y-4">
                    <PurchaseClaimList
                        claims={myClaims}
                        selectedClaims={selectedClaims}
                        setSelectedClaims={setSelectedClaims}
                        onRedeemSuccess={() => {
                            fetchShopBalances();
                            setSelectedTab("redeemed");
                        }}
                    />
                </div>
            )}

            {selectedTab === "redeemrequest" && (
                <div className="space-y-4">
                    <RedemptionList />
                </div>
            )}

            {selectedTab === "redeemedlist" && (
                <div className="space-y-4">
                    <RedemptionList />
                </div>
            )}

            {selectedTab === "approvals" && (
                <div className="space-y-3">
                    {pendingClaims.length === 0 ? (
                        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 text-center">
                            <div className="text-3xl mb-2">✅</div>
                            <div className="text-gray-100 text-sm">No pending claim approvals</div>
                        </div>
                    ) : (
                        <>
                            <div className="text-xs text-gray-200 px-1">
                                {pendingClaims.length} pending · Swipe right to approve, left to reject, down for partial
                            </div>
                            {pendingClaims.map((claim) => (
                                <ShopApprovalCard
                                    key={claim._id}
                                    claim={claim}
                                    onRefresh={fetchPendingClaims}
                                />
                            ))}
                        </>
                    )}
                </div>
            )}

            {selectedTab === "claimapprovalhistory" && (
                <div className="space-y-4">
                    <MerchantClaimHistory />
                </div>
            )}

            {selectedTab === "approveredeem" && (
                <div className="space-y-3">
                    {requestedRedemption.length === 0 ? (
                        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 text-center">
                            <div className="text-3xl mb-2">💜</div>
                            <div className="text-gray-100 text-sm">No redemption requests</div>
                        </div>
                    ) : (
                        <>
                            <div className="text-xs text-gray-200 px-1">
                                {requestedRedemption.length} pending · Swipe right to approve, left to reject
                            </div>
                            {requestedRedemption.map((redeem) => (
                                <RedemptionCard
                                    key={redeem._id}
                                    redemption={redeem}
                                    onRefresh={loadRedemptions}
                                />
                            ))}
                        </>
                    )}
                </div>
            )}

            {selectedTab === "redeemedapporvalhistory" && (
                <div className="space-y-4">
                    <MerchantApprovalHistory/>
                </div>
            )}

            {selectedTab === "certificates" && <PDCertificateGenerator />}
 
            {selectedTab === "leaderboard" && (<PDContestLeaders />)}

            {selectedTab === "winners" && (<PDContestWinners />)}

            {selectedTab === "contest" && (<PDContest />)}

            {selectedTab === "flywheel" && (<StreakFlywheel />)}
        </div>
    );
};

export default PDStreakHome;
